import { TermsContainer } from '@components/Container';
import Content from '@components/Content';
import { H1 } from '@components/Heading';
import { Link } from '@components/Link';
import useImpersonate from '@State/Impersonate/useImpersonate';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const { impersonate } = useImpersonate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <TermsContainer $impersonate={!!impersonate}>
      <H1>{t('privacypolicy_h1')}</H1>
      <Content>
        <h2>Cookies</h2>
        <p>
          <strong>Här kan du läsa mer om hur vi hanterar cookies i Kundportalen.</strong>
        </p>
        <p>
          För att förbättra Din upplevelse av Kundportalen använder Remondis cookies och liknande
          teknik. Kundportalen är en webbplats med adressen portal.rmdse.se. Du accepterar
          användningen av cookies och liknanden teknik på Kundportalen genom att välja
          “Acceptera”-knappen i cookie-bannern längst ned på sidan då du besöker den för första
          gången.
        </p>
        <h3>Vad är cookies?</h3>
        <p>
          En cookie är en liten textfil som en webbplats begär att få spara på besökarens dator,
          surfplatta eller mobiltelefon. På många webbplatser används cookies för att ge besökarna
          tillgång till olika funktioner. Informationen i cookies kan också användas för att följa
          användarens surfande på webbplatser som använder samma cookie.
        </p>
        <p>
          Termen cookie är den mest använda termen men med &quot;cookies&quot; menas här också andra
          tekniker, såsom webblagring som används i stationär programvara eller mobila enheter, s.k.
          local storage. När vi hänvisar till &quot;cookies&quot; i denna policy menar vi nedan även
          sådan typ av teknik.
        </p>
        <h3>Det finns några olika kategorier av cookies</h3>
        <p>
          <strong>Absolut nödvändiga cookies</strong> är cookies som är nödvändiga för att
          Kundportalen ska fungera. Dessa kan inte stängas av. De är inställda för att exempelvis
          hålla reda på en vald funktion i tjänsten. Dessa cookies kallas också Sessions-cookies
          vilka lagras tillfälligt på din dator men upphör att gälla när du stänger din webbläsare.
          Dessa cookies lagrar inte någon personligt identifierbar information.
        </p>
        <p>
          <strong>Funktionella cookies</strong> inkluderar tekniken Local Storage och är cookies som
          gör det möjligt för oss att ge Dig personlig anpassning och förbättrad funktionalitet i
          Kundportalen. De används för att komma ihåg vilka val du gjort. Då behöver du inte upprepa
          dina inställningar vid ett senare besök. De kan fastställas av oss eller av
          tredjepartsleverantörer vars tjänster vi har lagt till på våra sidor. Om du inte tillåter
          dessa cookies kanske vissa eller alla av funktionerna inte fungerar i Kundportalen.
        </p>
        <p>
          <strong>Förstapartscookies</strong> är cookies som ställts in av webbplatsen som du
          besöker, antingen av oss eller av en tredje part på vår begäran.
        </p>
        <p>
          <strong>Tredjepartscookies</strong> är cookies som ställs in av en annan part än
          webbplatsen du besöker.
        </p>
        <h3>Cookies som används i Kundportalen</h3>
        <p>
          I listan här nedan beskriver vi de cookies som används, liksom syftet och lagringsperioden
          för varje cookie:
        </p>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Description</th>
                <th>Purpose</th>
                <th>Storage period</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>cookie-consent</td>
                <td>portal.rmdse.se</td>
                <td>Set when the user accepts the cookie policy</td>
                <td>Used to decide if the cookie consent dialog should be displayed or not</td>
                <td>1 year</td>
              </tr>
              <tr>
                <td>AWSALBCORS</td>
                <td>login.portal.rmdse.se</td>
                <td>Sticky sessions for AWS Load Balancer with specific CORS settings</td>
                <td>
                  Used to map the session to the target if CORS settings is set to{' '}
                  <em>SameSite=None; Secure</em>
                </td>
                <td>7 days</td>
              </tr>
              <tr>
                <td>AWSALB</td>
                <td>login.portal.rmdse.se</td>
                <td>Sticky sessions for AWS Load Balancer</td>
                <td>Used to map the session to the target</td>
                <td>7 days</td>
              </tr>
              <tr>
                <td>KC_RESTART</td>
                <td>login.portal.rmdse.se</td>
                <td>Makes it possible to restart a session if there is a client timeout</td>
                <td>Used to re-create authentication flow when browser root session is expired</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>AUTH_SESSION_ID_LEGACY</td>
                <td>login.portal.rmdse.se</td>
                <td>Session ID</td>
                <td>Used to identify a session</td>
                <td>Session</td>
              </tr>
              <tr>
                <td>AUTH_SESSION_ID</td>
                <td>login.portal.rmdse.se</td>
                <td>Session ID</td>
                <td>Used to identify a session</td>
                <td>Session</td>
              </tr>
            </tbody>
          </table>
        </div>
        <h3>Hur vi använder cookies</h3>
        <p>
          Under varje besök på Kundportalen hämtas de sidor du besöker, tillsammans med eventuella
          cookies, till din enhet. Remondis kommer inte att använda cookies för att samla in
          personligt identifierbar information om dig.
        </p>
        <h3>Hantera cookies</h3>
        <p>
          Du kan återkalla ditt samtycke för att vi ska använda cookies när du vill genom att radera
          cookies från vår domän och ladda om webbplatsen. Observera att om du vägrar cookies kanske
          du inte kan använda alla funktioner på webbplatsen.
        </p>
        <p>
          Om du vill begränsa eller blockera de cookies som ställs av Remondis kan du göra detta via
          dina webbläsarinställningar på din dator eller genom att inte acceptera cookies på
          webbplatsen. Det kan skilja sig åt hur denna inställning ska göras. Varje webbläsare eller
          mobilapp är olika, så kolla &quot;Hjälp&quot;-menyn i din webbläsare för att lära dig hur
          du ändrar dina cookie-inställningar.
        </p>
        <h3>Kontaktinformation</h3>
        <p>
          Om du har några frågor kring cookies policyn, tveka inte att kontakta Remondis via
          kontaktuppgifterna nedan eller skicka ett e-postmeddelande till{' '}
          <Link href="mailto:kundservice@remondis.se" title="Kontakta kundtjänst via e-post">
            kundservice@remondis.se
          </Link>
          .
        </p>

        <h2>Dataskydd för mobilapplikationen REMONDIS Sweden</h2>
        <p>
          Denna integritetspolicy gäller även mobilapplikationen REMONDIS Sweden, som publiceras av
          REMONDIS SWEDEN via Google Play och Apple App Store.
        </p>
        <p>
          Mobilapplikationen används för att ge användare tillgång till REMONDIS Swedens tjänster
          och funktioner. I samband med användningen av appen kan personuppgifter samlas in,
          behandlas och delas i enlighet med denna policy.
        </p>

        <h3>Vilka uppgifter vi samlar in</h3>
        <p>
          Beroende på vilka funktioner som används kan appen behandla följande kategorier av
          personuppgifter:
        </p>
        <p>
          <strong>Kontaktuppgifter</strong>
        </p>
        <ul>
          <li>Namn</li>
          <li>E-postadress</li>
          <li>Telefonnummer</li>
          <li>Företagsnamn</li>
        </ul>
        <p>
          <strong>Användaruppgifter</strong>
        </p>
        <ul>
          <li>Kundnummer eller användar-ID</li>
          <li>Inloggningsuppgifter</li>
          <li>Kontoinställningar</li>
        </ul>
        <p>
          <strong>Enhets- och teknisk information</strong>
        </p>
        <ul>
          <li>IP-adress</li>
          <li>Enhetsmodell</li>
          <li>Operativsystem</li>
          <li>Appversion</li>
          <li>Språkinställningar</li>
          <li>Logg- och diagnostikdata</li>
        </ul>
        <p>
          <strong>Användningsdata</strong>
        </p>
        <ul>
          <li>Hur appen används</li>
          <li>Besökta funktioner</li>
          <li>Datum och tid för aktiviteter</li>
          <li>Felrapporter och prestandainformation</li>
        </ul>
        <p>
          <strong>Platsdata</strong>
        </p>
        <p>
          Om funktionalitet i appen kräver platsinformation samlas sådan endast in efter att
          användaren lämnat sitt uttryckliga samtycke via enhetens behörighetsinställningar.
        </p>

        <h3>Hur vi använder uppgifterna</h3>
        <p>Personuppgifter behandlas för följande ändamål:</p>
        <ul>
          <li>Tillhandahålla och administrera appens funktioner</li>
          <li>Hantera användarkonton</li>
          <li>Leverera tjänster som användaren begär</li>
          <li>Identifiera och åtgärda tekniska problem</li>
          <li>Förbättra appens funktionalitet, kvalitet och användarupplevelse</li>
          <li>Kommunicera med användaren om tjänster och supportärenden</li>
          <li>Uppfylla rättsliga skyldigheter</li>
        </ul>
        <p>Den rättsliga grunden för behandlingen utgörs av:</p>
        <ul>
          <li>Fullgörande av avtal enligt artikel 6.1 b GDPR</li>
          <li>Samtycke enligt artikel 6.1 a GDPR</li>
          <li>Berättigat intresse enligt artikel 6.1 f GDPR</li>
          <li>Rättsliga skyldigheter enligt artikel 6.1 c GDPR</li>
        </ul>

        <h3>Delning av information</h3>
        <p>Vi säljer inte personuppgifter.</p>
        <p>Personuppgifter kan delas med:</p>
        <ul>
          <li>REMONDIS-koncernens bolag</li>
          <li>IT- och molntjänstleverantörer</li>
          <li>Drift- och supportleverantörer</li>
          <li>Analys- och statistikleverantörer</li>
          <li>Myndigheter när lag kräver detta</li>
        </ul>
        <p>
          All delning sker i enlighet med GDPR och endast i den utsträckning som krävs för de syften
          som anges i denna policy.
        </p>

        <h3>Analys och diagnostik</h3>
        <p>
          Appen kan använda analys- och diagnostikverktyg för att förstå hur appen används,
          förbättra funktionalitet och upptäcka tekniska problem. Dessa verktyg kan behandla
          enhetsidentifierare, användningsdata och teknisk information.
        </p>
        <p>
          Om sådan behandling kräver samtycke kommer användaren att få möjlighet att lämna eller
          återkalla sitt samtycke.
        </p>

        <h3>Datalagring</h3>
        <p>
          Personuppgifter lagras endast så länge det är nödvändigt för de ändamål som anges i denna
          policy eller så länge lag kräver.
        </p>
        <p>När uppgifterna inte längre behövs raderas eller anonymiseras de på ett säkert sätt.</p>

        <h3>Barns integritet</h3>
        <p>
          Applikationen REMONDIS Sweden riktar sig inte till barn under 13 år. Vi samlar inte
          medvetet in personuppgifter från barn.
        </p>

        <h3>Datasäkerhet</h3>
        <p>
          Vi använder organisatoriska och tekniska säkerhetsåtgärder för att skydda personuppgifter
          mot obehörig åtkomst, förlust, ändring eller otillåten spridning.
        </p>

        <h3>Användarens rättigheter</h3>
        <p>Användare av REMONDIS Sweden har rätt att:</p>
        <ul>
          <li>Begära tillgång till sina personuppgifter</li>
          <li>Begära rättelse av felaktiga uppgifter</li>
          <li>Begära radering av personuppgifter</li>
          <li>Invända mot behandling</li>
          <li>Begära begränsning av behandling</li>
          <li>Begära dataportabilitet</li>
          <li>Återkalla lämnade samtycken</li>
        </ul>
        <p>
          Begäran om att återkalla eller invända skickas till:{' '}
          <Link href="mailto:dataskydd@remondis.se" title="Kontakta dataskydd via e-post">
            dataskydd@remondis.se
          </Link>
        </p>
      </Content>
    </TermsContainer>
  );
};

export default PrivacyPolicy;
